import { reverse } from 'named-urls'
import React from 'react'

import {
  AlertModal,
  ButtonClean,
  ConfirmModal,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import { useOffer } from '@modules/Dashboard/Product/hooks/useOffer'
import { routing } from '@utils/routing'

import { useOfferCombinedDetachMutation } from '../../../CombinedProduct/queries/useOfferCombinedDetachMutation'
import { useCustomizationV4Query } from '../../hooks/useCustomizationV4Query'
import { useProductBySlugQuery } from '../../hooks/useProductBySlugQuery'
import { UnavailableProductModalAlert } from './UnavailableProductModalAlert'

type DesignEditButtonProps = {
  offerId: string
  customizationId: string
}

export const DesignEditButton = ({ customizationId, offerId }: DesignEditButtonProps) => {
  const { open } = useModalContext()
  const mq = useMediaQueryContext()
  const { offerCombinedDetachMutation } = useOfferCombinedDetachMutation()

  const { customizationV4Query } = useCustomizationV4Query([customizationId])
  const { productBySlugQuery } = useProductBySlugQuery(
    [customizationV4Query.data?.product.slug!!],
    {
      enabled: !!customizationV4Query.data?.product.slug,
    },
  )
  const { offer } = useOffer(offerId)

  if (!customizationV4Query.data || !productBySlugQuery.data) {
    return null
  }

  if (productBySlugQuery.data.customizationType === 'REQUESTABLE') {
    return null
  }

  const handleEditClick = () => {
    if (offer?.parentOffer) {
      const combinedUrl = reverse(routing.products.combined.combinedId, {
        combinedId: offer.parentOffer?.id,
      })

      return open(ConfirmModal, {
        title: 'Unable to edit design',
        text: (
          <p>
            This product is part of a combined listing (
            <a
              href={combinedUrl}
              style={{
                display: 'inline',
                textDecoration: 'underline',
              }}
            >
              {offer.parentOffer.name}
            </a>
            ). In order to edit its design you need to detach this product from the combined
            listing.
          </p>
        ),
        confirmLabel: 'Remove from combined listing',
        cancelLabel: 'Cancel',

        onConfirm: async () => {
          if (!offer.id) {
            throw new Error('Offer is not defined')
          }

          if (!offer.parentOffer) {
            throw new Error('Offer parentOffer is not defined')
          }

          await offerCombinedDetachMutation.mutateAsync([offer.parentOffer.id, offer.id])

          window.location.href = combinedUrl
        },
      })
    }

    if (productBySlugQuery.data.allowedForDesigner) {
      if (customizationV4Query.data?.designerVersion === 'PRINTFUL') {
        const designerPrintfulUrl = reverse(routing.printfulEmbededDesigner.customizationEdit, {
          customizationId,
          productId: offerId,
        })

        window.location.assign(designerPrintfulUrl)

        return
      }

      if (mq.tablet) {
        const designerV3Url = reverse(routing.productDesignerV3.customizationEdit, {
          customizationId,
          productId: offerId,
        })

        window.location.assign(designerV3Url)

        return
      }

      return open(AlertModal, {
        title: 'We suggest using desktop to create your products',
        text: "We're working on product customizing on mobile. For now you can only edit design on a larger screen.",
      })
    }

    return open(UnavailableProductModalAlert, {
      text: (
        <p>
          Product {productBySlugQuery.data.name} is not currently available and designs cannot be
          edited. We're working on bringing this product back as soon as we can. If you have any
          questions, please contact us at{' '}
          <ButtonClean label="support@fourthwall.com" href="mailto:support@fourthwall.com" />.
        </p>
      ),
      handleEditClick: () => {
        if (customizationV4Query.data?.designerVersion === 'PRINTFUL') {
          const designerPrintfulUrl = reverse(routing.printfulEmbededDesigner.customizationEdit, {
            customizationId,
            productId: offerId,
          })

          window.location.assign(designerPrintfulUrl)

          return
        }

        if (mq.tablet) {
          const designerV3Url = reverse(routing.productDesignerV3.customizationEdit, {
            customizationId,
            productId: offerId,
          })

          window.location.assign(designerV3Url)
        }
      },
    })
  }

  return <ButtonClean label="Edit product design" onClick={handleEditClick} />
}
